import type {
  ConsoleOrganization,
  EntitlementLimitKey,
  OrganizationAccount,
  OrganizationEntitlements,
  ProductCapability,
  SubscriptionStatus,
} from "./queries";

/**
 * DELINQUENT_STATUSES are the subscription lifecycle states in which a payment is owed or the
 * subscription has ended: past-due, unpaid and canceled. The set is the console's own, written
 * for its nudges and banners, and no server set is its twin: the API decides what a subscription
 * grants from its own status rules. A Set for O(1) membership in the selector.
 */
const DELINQUENT_STATUSES = new Set<SubscriptionStatus>(["past-due", "unpaid", "canceled"]);

/**
 * isDelinquentStatus reports whether a subscription lifecycle status is delinquent: past-due,
 * unpaid, or canceled. The console's add-card nudge (selectRequiresPaymentMethod) stands down
 * for such a subscription, because the billing-state banner already reports it with its own way
 * out. Pure so the selector and a render-free caller share one rule.
 */
export const isDelinquentStatus = (status: SubscriptionStatus): boolean =>
  DELINQUENT_STATUSES.has(status);

/**
 * LimitValue is the resolved state of one limit of an agreement: `unlimited` when the
 * dimension is uncapped, otherwise the numeric ceiling. A missing entitlement (no org
 * loaded, or a read that carries no entitlements) resolves to a finite 0, so a consumer
 * renders a concrete meter rather than an unbounded one for an org whose limits it cannot see.
 */
export type LimitValue = { unlimited: true } | { unlimited: false; value: number };

/**
 * CUSTOM_ENTITLEMENT_SOURCE is the entitlement source the staff override records:
 * the organization's billing is a comped, invoiced, or otherwise manually settled
 * arrangement managed by Mountaya staff. Mirrors the backend
 * `types.EntitlementSourceCustom` value.
 */
const CUSTOM_ENTITLEMENT_SOURCE = "custom";

/**
 * SELF_SERVE_ENTITLEMENT_SOURCE is the entitlement source of an organization billed
 * self-serve: its entitlements follow from its Data API subscription. Mirrors the backend
 * `types.EntitlementSourceSubscription` value.
 */
const SELF_SERVE_ENTITLEMENT_SOURCE = "subscription";

/**
 * isCustomBilled reports whether an entitlement set marks the organization as
 * custom-billed (staff-managed billing). Such an organization cannot mutate its own
 * billing — the backend rejects product, payment-method and billing-info writes
 * with a 403 billing.custom_billed — so the billing surface hides those controls
 * and renders read-only status instead. Pure (no React) so render-free callers and
 * components share one rule.
 */
export const isCustomBilled = (entitlements: OrganizationEntitlements | undefined): boolean =>
  entitlements?.source === CUSTOM_ENTITLEMENT_SOURCE;

/**
 * hasCapability reports whether an entitlement set grants a capability. A missing
 * or false entry means not granted. Pure (no React) so render-free callers and the
 * selector hook share one rule.
 */
export const hasCapability = (
  entitlements: OrganizationEntitlements | undefined,
  capability: ProductCapability,
): boolean => entitlements?.capabilities[capability] === true;

/**
 * resolveLimit projects one limit key into a LimitValue, for display: a null value means the
 * dimension is uncapped and a number is the ceiling. The organization's entitlements always
 * carry all seven keys, each forced by staff or at its default, so the only missing value is a
 * read that carries no entitlements, and it resolves as a ceiling of 0 rather than as
 * unlimited. That is fail-closed: the backend's own check is `limit < 0 || count < limit`, so
 * a 0 admits nothing, and a consumer that would otherwise render an unbounded meter for an
 * org whose limits it cannot see reports no headroom instead. The missing case must not be
 * folded into the null one with a `??`, which treats the two alike and would report a
 * dimension nobody stated as uncapped.
 *
 * A negative number is uncapped too, for the same reason and read off the same backend
 * check: `limit < 0` admits everything. Null is how the wire spells unlimited now, but a
 * row written before that carries -1, and the two must not disagree here — a stale -1
 * would otherwise render "3 of -1" and make every client-side `count < limit` gate deny
 * what the backend admits. This mirrors the organization security surface, which resolves
 * a leaked sentinel the same way. Pure so the meter rendering and the selector hook share
 * one rule.
 */
export const resolveLimit = (
  entitlements: OrganizationEntitlements | undefined,
  key: EntitlementLimitKey,
): LimitValue => {
  const raw = entitlements?.limits[key];
  if (raw === null || (raw !== undefined && raw < 0)) {
    return { unlimited: true };
  }

  return { unlimited: false, value: raw ?? 0 };
};

/**
 * selectHasCapability reads a capability off a loaded account (the account query result). It is
 * the render-free projection the useHasCapability hook wraps, so a component already holding the
 * account can branch without re-subscribing. An account not loaded yet grants nothing.
 */
export const selectHasCapability = (
  account: OrganizationAccount | undefined,
  capability: ProductCapability,
): boolean => hasCapability(account?.entitlements, capability);

/**
 * selectLimit reads one limit of the organization's agreement off a loaded account into a
 * LimitValue, null reading as unlimited. The render-free projection the useLimit hook wraps.
 */
export const selectLimit = (
  account: OrganizationAccount | undefined,
  key: EntitlementLimitKey,
): LimitValue => resolveLimit(account?.entitlements, key);

/**
 * selectCanManageBilling reads the account's `can_manage_billing` flag — true only for an
 * organization owner or admin, and false for a Mountaya staff member reading through the
 * membership waiver. The billing page renders its edit controls only when this is true; a
 * contributor/viewer sees the read-only surface. An account not loaded yet resolves to false, so
 * the controls stay hidden until the membership is confirmed.
 */
export const selectCanManageBilling = (account: OrganizationAccount | undefined): boolean =>
  account?.can_manage_billing === true;

/**
 * selectRequiresPaymentMethod reports whether the org needs a card on file: it is billed
 * self-serve (its entitlements are derived from a subscription, not set by an agreement), its
 * creator did not ask for an agreement that is still being prepared (`awaiting_agreement`),
 * and it has no default payment method. Only the Data API and the MCP server are sold
 * self-serve, and the organization's first saved card is what opens their metered
 * subscription, so this is the proactive mirror of the Data API's card gate, which answers a
 * 402 `PAYMENT_REQUIRED` keyed `billing.payment_method_required`: when true, the SPA can nudge
 * an owner to add a card up front instead of letting a Data API request answer 402.
 *
 * It reads both halves of the organization: `awaiting_agreement` off the organization, which
 * every reader of it is answered, and the rest off the account, which only a console session
 * reads. No card reads off the subscription summary: `has_payment_method` false, or no summary
 * at all, which is an organization whose first card has not opened its subscription yet. A
 * delinquent subscription (past-due, unpaid or canceled) resolves to false, since its own
 * banner state handles it. An organization billed by agreement, one awaiting its agreement,
 * and either half not loaded yet resolve to false.
 */
export const selectRequiresPaymentMethod = (
  organization: ConsoleOrganization | undefined,
  account: OrganizationAccount | undefined,
): boolean => {
  if (
    organization === undefined ||
    account?.entitlements.source !== SELF_SERVE_ENTITLEMENT_SOURCE
  ) {
    return false;
  }

  if (organization.awaiting_agreement) {
    return false;
  }

  const summary = account.subscription;
  const status = summary?.api?.status;
  if (status !== undefined && isDelinquentStatus(status)) {
    return false;
  }

  return summary?.has_payment_method !== true;
};
